// Single source of truth for the Inventory module's tabs.
//
// Inventory keeps one base route (`/inventory`) and selects the visible tab with the `tab` query
// parameter, so switching tabs never changes the pathname and therefore never remounts the route.
// Everything that links into Inventory (nav config, sidebar, breadcrumbs, cross-module drill-ups)
// should build its target from here instead of hardcoding a path.

export const INVENTORY_BASE_PATH = '/inventory';
export const INVENTORY_TAB_PARAM = 'tab';
export const DEFAULT_INVENTORY_TAB = 'overview';

export const INVENTORY_TABS = ['overview', 'resources', 'key-insight', 'explorer'];

/**
 * Build an Inventory URL for a tab, optionally carrying extra query parameters.
 * The `tab` param is written first so the URLs read predictably, e.g.
 * `/inventory?tab=key-insight&runtime=nodejs18.x`.
 *
 * @param {string} [tab] one of INVENTORY_TABS; anything else falls back to the default tab
 * @param {Record<string, string | number | undefined | null>} [extraParams] appended after `tab`
 * @returns {string} path + search, suitable for `history.push` / `<Link to>` / breadcrumb `path`
 */
export function inventoryTabPath(tab = DEFAULT_INVENTORY_TAB, extraParams = undefined) {
  const params = new URLSearchParams();
  params.set(INVENTORY_TAB_PARAM, INVENTORY_TABS.includes(tab) ? tab : DEFAULT_INVENTORY_TAB);

  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      params.set(key, String(value));
    });
  }

  return `${INVENTORY_BASE_PATH}?${params.toString()}`;
}

// Prebuilt paths for each tab — use these when there are no extra params.
export const INVENTORY_TAB_PATHS = {
  overview: inventoryTabPath('overview'),
  resources: inventoryTabPath('resources'),
  keyInsight: inventoryTabPath('key-insight'),
  explorer: inventoryTabPath('explorer'),
};

/**
 * Resolve the active Inventory tab from a location search string. Missing or unknown values
 * resolve to the default tab, so a bare `/inventory` renders Overview.
 *
 * @param {string} [search] `location.search` (leading `?` optional)
 * @returns {string} a member of INVENTORY_TABS
 */
export function resolveInventoryTab(search = '') {
  const tab = new URLSearchParams(search).get(INVENTORY_TAB_PARAM);
  return INVENTORY_TABS.includes(tab) ? tab : DEFAULT_INVENTORY_TAB;
}
